import React, { useState, useEffect } from 'react';
import { Card, Table, Modal, Space, Row, Col, Grid, notification, Empty } from 'antd';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import DraggableTable from '../../../components/Tables/DraggableTable';
import { usePathQuestions } from '../usePathQuestions';
import { generateQuestionConfig } from '../PathStepper/questionConfig';

import {
  Eye,
  PlusCircle,
  Trash2,
  Move
} from 'lucide-react';

const { useBreakpoint } = Grid;

// Replace icons with straightforward text labels
const TYPE_LABELS = {
  info: 'Info',
  slider: 'Slider',
  yesNo: 'Yes/No',
  allocation: 'Allocation',
  lean: 'Lean'
};

const PreviewModal = ({ question, visible, onClose }) => {
  if (!question) return null;
  console.log(question);
  if (question?.fields?.name==='info'){
    notification.error({
      title: 'Preview Not Available',
      description: 'Info questions cannot be previewed yet'
    });
    onClose();
    return null;
  }

  const questionConfig = generateQuestionConfig(question.original || question);
  const Component = questionConfig.component;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ height: '90vh' }}
      title="Question Preview"
    >
      <Component value={null} onChange={() => {}} />
    </Modal>
  );
};

const PathQuestionManager = ({ selectedIds = [], onSubmit }) => {
  const { questionsLibrary, isLoading } = usePathQuestions();
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const bp = useBreakpoint();

  useEffect(() => {
    if (questionsLibrary && selectedIds.length) {
      const questions = selectedIds
        .map(id => questionsLibrary.find(q => q.sys.id === id))
        .filter(Boolean)
        .map(q => ({
          key: q.sys.id,
          ...q.fields,
          original: q
        }));
      setSelectedQuestions(questions);
    }
  }, [questionsLibrary, selectedIds]);

  const handleSelectQuestion = (question) => {
    const newQuestion = {
      key: question.sys.id,
      ...question.fields,
      original: question
    };
    const newQuestions = [...selectedQuestions, newQuestion];
    setSelectedQuestions(newQuestions);
    onSubmit(newQuestions.map(q => q.key));
  };

  const handleRemoveQuestion = (questionId) => {
    const newQuestions = selectedQuestions.filter(q => q.key !== questionId);
    setSelectedQuestions(newQuestions);
    onSubmit(newQuestions.map(q => q.key));
  };

  const handleReorder = (newDataOrUpdater) => {
    const newQuestions =
      typeof newDataOrUpdater === 'function'
        ? newDataOrUpdater(selectedQuestions)
        : newDataOrUpdater;

    setSelectedQuestions(newQuestions);
    onSubmit(newQuestions.map(q => q.key));
  };

  const getCommonColumns = (isSelected) => [
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Space>
          {isSelected && (
            <Move
              size={18}
              style={{ cursor: 'move' }}
              aria-label="Reorder"
            />
          )}
          {isSelected ? (
            <>
              <Trash2
                size={18}
                onClick={() => handleRemoveQuestion(record.key)}
                style={{ cursor: 'pointer' }}
                aria-label="Remove"
              />
              <Eye
                size={18}
                onClick={() => setPreviewQuestion(record)}
                style={{ cursor: 'pointer' }}
                aria-label="Preview"
              />
            </>
          ) : (
            <>
              <PlusCircle
                size={18}
                onClick={() => handleSelectQuestion(record)}
                style={{ cursor: 'pointer' }}
                aria-label="Add"
              />
              <Eye
                size={18}
                onClick={() => setPreviewQuestion(record)}
                style={{ cursor: 'pointer' }}
                aria-label="Preview"
              />
            </>
          )}
        </Space>
      )
    },
    {
      title: 'Question',
      dataIndex: isSelected ? 'name' : ['fields', 'name'],
      key: 'name',
      width: '25%'
    },
    {
      title: 'Type',
      dataIndex: isSelected ? 'type' : ['fields', 'type'],
      key: 'type',
      width: '15%',
      render: (type) => TYPE_LABELS[type] || type
    },
    {
      title: 'Step Title',
      dataIndex: isSelected ? 'stepTitle' : ['fields', 'stepTitle'],
      key: 'stepTitle',
      width: '30%'
    }
  ];

  if (!questionsLibrary) return null;

  const AvailableItemTable = () => {
    const dataSource = questionsLibrary.filter(
      q => !selectedQuestions.find(sq => sq.key === q.sys.id)
    );
    return (
      <Table
        rowKey={(record) => record.sys.id}
        columns={getCommonColumns(false)}
        dataSource={dataSource}
        loading={isLoading}
        pagination={{ pageSize: 10 }}
      />
    );
  };

  const SelectedItemTable = () => (
    <DraggableTable
      columns={getCommonColumns(true)}
      data={selectedQuestions}
      setData={handleReorder}
      tableProps={{
        loading: isLoading,
        pagination: false
      }}
    />
  );

  return (
    <>
      <Card bordered={false}>
        <Row wrap={false} justify="space-between" gutter={32}>
          {/* Selected questions on the left */}
          {bp.xl && !previewQuestion && (
            <Col span={8}>
              {selectedIds?.length > 0 && <SelectedItemTable />}
              {selectedIds?.length===0&&(
                  <div className={'py-6'}  >
                    <Empty description={''} image={Empty.PRESENTED_IMAGE_SIMPLE}>
                    No Questions Added
                  </Empty>
                  </div>
              )}
            </Col>
          )}
          {/* Available questions on the right */}
          <Col span={16}>
            <AvailableItemTable />
          </Col>
        </Row>
      </Card>
      <PreviewModal
        question={previewQuestion}
        visible={!!previewQuestion}
        onClose={() => setPreviewQuestion(null)}
      />
    </>
  );
};

export default PathQuestionManager;
